import {noop} from '@benchling/js-utils';
import React from 'react';

import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import BulkCreateRegistryEntitiesFlowClass from 'bulk-import/BulkCreateRegistryEntitiesFlow';
import {openBulkCreateTrialSamplesFlow} from 'bulk-import/BulkCreateTrialSamplesFlow';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  FolderCreateDropdownEnhancedProps,
  FolderCreateDropdownProps,
} from 'file-browser/FolderCreateDropdown';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  CreateMenuComplexPolymersQuery,
  CreateMenuComplexPolymersQueryVariables,
} from 'modbio/complex-polymers/__generated/CreateMenuComplexPolymersQuery';
import {
  CREATE_MENU_COMPLEX_POLYMERS_QUERY,
  getComplexPolymerSchemaConfigs,
  getComplexPolymerSchemaConfiguration,
  isComplexPolymerSchema,
} from 'modbio/complex-polymers/ComplexPolymerSchemaUtil';
import {
  openBulkReimportProteins,
  openBulkReimportSequences,
  openBulkUpdateBioentitiesModal,
  openBulkUpdateMixtureIngredientsModal,
} from 'nav/CreateDropdownActions';
import NavStore from 'nav/NavStore';
import useTagSchemaAwareObjectCreateQuery from 'nav/sidepanel/listing/hooks/useTagSchemaAwareObjectCreateQuery';
import CreateAndRegisterBioentityModal from 'registry/create-bioentity/CreateAndRegisterBioentityModal';
import * as CreateBioentityUtil from 'registry/create-bioentity/CreateBioentityUtil';
import {toModelTypeConstant} from 'tag-schema/util/ModelTypeConversions';
import {
  isDNAOligoSchemaGraphQL,
  isDNASequenceSchemaGraphQL,
  isRNAOligoSchemaGraphQL,
  isRNASequenceSchema,
} from 'tag-schema/util/TagSchemaUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';
import Section from 'ui-library/menus/Section';
import runModal from 'ui-library/runModal';
import {getRenderStarredOptionContent} from 'util/getRenderStarredOptionContent';
import sortedBy from 'util/sortedBy';

import {TagSchemaListingGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingGraphQL';

const REQUIRED_PERMISSIONS = ['objectsCreatable', 'objectsRegisterable'] as const;

function LoadingIconButton({
  isLoading,
  dropLeft,
  icon,
}: {
  isLoading?: boolean;
  dropLeft: boolean;
  icon: BenchlingIconName;
}): JSX.Element {
  return (
    <Tooltip text="Loading..." isDisabled={!isLoading} disableContentSelection={true}>
      <Button
        buttonStyle={dropLeft ? ButtonStyle.SECONDARY : ButtonStyle.TERTIARY_BLUE}
        icon={icon}
        size={ButtonSize.SMALL}
        text={dropLeft ? 'Create' : null}
        disabled={isLoading}
      />
    </Tooltip>
  );
}

export function RegistryCreateMenu(
  props: FolderCreateDropdownProps & FolderCreateDropdownEnhancedProps
): JSX.Element {
  const complexPolymersSchemasResult = useSkippableBenchlingQuery<
    CreateMenuComplexPolymersQuery,
    CreateMenuComplexPolymersQueryVariables
  >(CREATE_MENU_COMPLEX_POLYMERS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES,
  });

  const allComplexPolymerSchemaConfigs = getComplexPolymerSchemaConfigs(complexPolymersSchemasResult);
  const renderOptionContentFn = getRenderStarredOptionContent(
    props.toggleTagSchemaStar,
    props.starredSchemaIds,
    {getId: (optionData: OptionData) => optionData.value.id}
  );

  function createAndRegisterBioentity(tagSchema: TagSchemaListingGraphQL): void {
    const complexPolymerSchemaConfig = getComplexPolymerSchemaConfiguration(
      tagSchema.id,
      allComplexPolymerSchemaConfigs
    );
    runModal(CreateAndRegisterBioentityModal, {
      createAndRegisterBioentityArgs: {
        ...CreateBioentityUtil.getDefaultCreateAndRegisterBioentityArgs(),
        tagSchemaId: tagSchema.id,
        complexPolymerSchemaConfig,
      },
    });
  }

  function getTagSchemaOption(
    name: string,
    folderItemType: string,
    schemaTypeFilterPredicate?: (tagSchema: TagSchemaListingGraphQL) => boolean,
    iconOverwrite?: BenchlingIconName
  ): JSX.Element | null {
    const schemas = props.tagSchemas?.filter(
      (tagSchema) =>
        (schemaTypeFilterPredicate
          ? schemaTypeFilterPredicate(tagSchema)
          : tagSchema.folderItemType === folderItemType) &&
        REQUIRED_PERMISSIONS.every((permission) => tagSchema.permissions[permission])
    );
    if (props.starredSchemaIds && schemas && schemas.length > 0) {
      const sortedSchemas = sortedBy(schemas, [
        (schema) => (props.starredSchemaIds!.includes(schema.id) ? 0 : 1),
      ]);
      const optionDatas: Array<OptionData> = sortedSchemas.map((schema) => ({
        icon: schema.iconId as BenchlingIconName,
        name: schema.name,
        value: schema,
      }));
      return (
        <Option
          icon={iconOverwrite ?? (folderItemType as BenchlingIconName)}
          dataTestAttrs={{component: 'RegistryCreateMenu', element: 'createSchemaMenuItem'}}
        >
          {name}
          <Menu>
            {optionDatas.map((optionData) => (
              <OptionContainer
                className="schema-option"
                key={optionData.value.id}
                onSelect={createAndRegisterBioentity}
                value={optionData.value}
                dataTestAttrs={{component: 'RegistryCreateMenu', element: 'createSchemaSubMenuItem'}}
              >
                {renderOptionContentFn(optionData, false)}
              </OptionContainer>
            ))}
          </Menu>
        </Option>
      );
    }
    return null;
  }

  function handleOpenBulkCreateRegistryEntitiesFlow(): void {
    const registry = NavStore.getSource(props.folder.source!.id);
    BulkCreateRegistryEntitiesFlowClass.open({
      initialRegistry: registry,
      complexPolymerSchemaConfigurations: allComplexPolymerSchemaConfigs,
    });
  }

  function handleOpenBulkCreateTrialSamplesFlow(): void {
    const registry = NavStore.getSource(props.folder.source!.id);
    openBulkCreateTrialSamplesFlow({initialRegistry: registry});
  }

  function handleOpenBulkUpdateBioentitiesModal(): void {
    openBulkUpdateBioentitiesModal(props.folder, allComplexPolymerSchemaConfigs);
  }

  function handleOpenBulkUpdateMixtureIngredientsModal(): void {
    openBulkUpdateMixtureIngredientsModal(props.folder);
  }

  function handleOpenBulkReimportSequences(): void {
    openBulkReimportSequences(props.folder.id);
  }

  function handleOpenBulkReimportProteins(): void {
    openBulkReimportProteins(props.folder.id);
  }

  const menuProps = {
    width: Menu.WIDTHS.MEDIUM,
    height: Menu.HEIGHTS.MEDIUM,
    dropLeft: props.dropLeft,
  };

  const menu = (
    <Menu {...menuProps}>
      <Section>
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_IMPORT}) && (
          <Option
            icon="table"
            key="importEntities"
            onSelect={handleOpenBulkCreateRegistryEntitiesFlow}
            dataTestAttrs={{component: 'RegistryMenu', element: 'dropdownItem', key: 'importEntities'}}
          >
            Import entities
          </Option>
        )}
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_EDIT}) && (
          <Option icon="table" onSelect={handleOpenBulkUpdateBioentitiesModal}>
            Update entities
          </Option>
        )}
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_IMPORT}) &&
          BenchlingConfig.REGISTRY_ENABLE_SAMPLE_INGESTION && (
            <Option
              icon="table"
              key="importTrialSamples"
              onSelect={handleOpenBulkCreateTrialSamplesFlow}
              dataTestAttrs={{
                component: 'RegistryMenu',
                element: 'dropdownItem',
                key: 'importTrialSamples',
              }}
            >
              Import trial samples
            </Option>
          )}
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES}) &&
          isFeatureEnabled({feature: RegistryFeature.REGISTRY_EDIT}) && (
            <Option icon="table" onSelect={handleOpenBulkUpdateMixtureIngredientsModal}>
              Import mixture ingredients
            </Option>
          )}
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_EDIT}) && (
          <Option icon="import" onSelect={handleOpenBulkReimportSequences}>
            Reimport DNA/RNA sequences
          </Option>
        )}
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_EDIT}) && (
          <Option icon="import" onSelect={handleOpenBulkReimportProteins}>
            Reimport AA sequences
          </Option>
        )}
      </Section>
      {isFeatureEnabled({feature: RegistryFeature.REGISTRY_CREATE}) && (
        <Section>
          {getTagSchemaOption('AA sequence', ModelTypeConstants.PROTEIN)}
          {getTagSchemaOption('DNA Sequence', ModelTypeConstants.SEQUENCE, isDNASequenceSchemaGraphQL)}
          {getTagSchemaOption(
            'RNA Sequence',
            ModelTypeConstants.SEQUENCE,
            isRNASequenceSchema,
            'rna-sequence'
          )}
          {getTagSchemaOption('DNA oligo', ModelTypeConstants.DNA_OLIGO, isDNAOligoSchemaGraphQL)}
          {getTagSchemaOption('RNA oligo', ModelTypeConstants.DNA_OLIGO, isRNAOligoSchemaGraphQL)}
          {BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES &&
            getTagSchemaOption(
              'Oligo duplex or oligo conjugate',
              ModelTypeConstants.BASIC_FOLDER_ITEM,
              (tagSchema: TagSchemaListingGraphQL) =>
                isComplexPolymerSchema(allComplexPolymerSchemaConfigs, tagSchema.id),
              'network-wired'
            )}
          {getTagSchemaOption(
            'Custom entity',
            ModelTypeConstants.BASIC_FOLDER_ITEM,
            (tagSchema: TagSchemaListingGraphQL) =>
              toModelTypeConstant(tagSchema.folderItemType) === ModelTypeConstants.BASIC_FOLDER_ITEM &&
              !isComplexPolymerSchema(allComplexPolymerSchemaConfigs, tagSchema.id)
          )}
          {isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES}) &&
            getTagSchemaOption('Mixture', ModelTypeConstants.MIXTURE)}
          {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE &&
            getTagSchemaOption('Molecule', ModelTypeConstants.MOLECULE)}
        </Section>
      )}
    </Menu>
  );

  const isLoading = complexPolymersSchemasResult.type === QueryResultType.PENDING;
  return (
    <div {...getTestProps({component: 'RegistryCreateMenu', element: 'dropdown'})}>
      <Dropdown menu={menu}>
        <div>
          <Tooltip
            text="Create..."
            placement={FloatingPlacement.RIGHT}
            disableContentSelection={true}
            isDisabled={isLoading}
          >
            <LoadingIconButton isLoading={isLoading} dropLeft={props.dropLeft} icon="compose" />
          </Tooltip>
        </div>
      </Dropdown>
    </div>
  );
}

export default function QueryingRegistryCreateMenu(
  props: Omit<FolderCreateDropdownProps, 'onOpenFolder'>
): JSX.Element | null {
  const {folder, ...restProps} = props;
  const result = useTagSchemaAwareObjectCreateQuery(folder);

  if (result == null) {
    return <LoadingIconButton isLoading={true} dropLeft={props.dropLeft} icon="compose" />;
  } else {
    const {tagSchemas, starredTagSchemaIds, handleToggleTagSchemaStar, folder: modifiedFolder} = result;

    return (
      <RegistryCreateMenu
        {...restProps}
        starredSchemaIds={starredTagSchemaIds}
        toggleTagSchemaStar={handleToggleTagSchemaStar}
        tagSchemas={tagSchemas}
        onOpenFolder={noop}
        folder={modifiedFolder}
      />
    );
  }
}
